import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'

type EmailQAReview = Database['public']['Tables']['email_qa_reviews']['Row']
type EmailQAInsert = Database['public']['Tables']['email_qa_reviews']['Insert']
type EmailQAUpdate = Database['public']['Tables']['email_qa_reviews']['Update']

interface EmailQAStore {
  reviews: EmailQAReview[]
  selectedReview: EmailQAReview | null
  isLoading: boolean
  error: string | null

  // Filters (simple)
  filterStatus: string | null
  filterRating: string | null
  filterReviewer: string | null

  // Actions
  fetchReviews: () => Promise<void>
  createReview: (review: EmailQAInsert) => Promise<EmailQAReview | null>
  updateReview: (id: string, updates: EmailQAUpdate) => Promise<void>
  setSelectedReview: (review: EmailQAReview | null) => void

  // Quick actions
  rateReview: (id: string, rating: number, notes?: string) => Promise<void>
  approveReview: (id: string, reviewer: string) => Promise<void>

  // Filters
  setFilterStatus: (status: string | null) => void
  setFilterRating: (rating: string | null) => void
  setFilterReviewer: (reviewer: string | null) => void
  clearFilters: () => void

  // Simple analytics
  getAnalytics: () => {
    totalReviews: number
    avgRating: number
    pendingCount: number
    approvedCount: number
    lowQualityCount: number
  }
}

export const useEmailQAStore = create<EmailQAStore>((set, get) => ({
  reviews: [],
  selectedReview: null,
  isLoading: false,
  error: null,

  // Filters
  filterStatus: null,
  filterRating: null,
  filterReviewer: null,

  fetchReviews: async () => {
    set({ isLoading: true, error: null })

    try {
      console.log('🔍 Fetching email_qa_reviews...')

      // Check auth session
      const { data: { session } } = await supabase.auth.getSession()
      console.log('🔐 Auth session:', session ? 'EXISTS' : 'MISSING', session?.user?.email)

      let query = supabase
        .from('email_qa_reviews')
        .select('*')
        .order('created_at', { ascending: false })

      // Apply filters
      const { filterStatus, filterRating, filterReviewer } = get()

      if (filterStatus) {
        query = query.eq('status', filterStatus)
      }
      if (filterRating) {
        const ratingValue = parseInt(filterRating)
        if (filterRating === 'high') {
          query = query.gte('rating', 8)
        } else if (filterRating === 'low') {
          query = query.lte('rating', 4)
        } else if (!isNaN(ratingValue)) {
          query = query.eq('rating', ratingValue)
        }
      }
      if (filterReviewer) {
        query = query.eq('reviewer', filterReviewer)
      }

      const { data, error } = await query

      console.log('📊 Query result:', { data, error, count: data?.length })

      if (error) {
        console.error('❌ Supabase error:', error)
        throw error
      }

      console.log('✅ Fetched', data?.length || 0, 'reviews')
      set({ reviews: data || [], isLoading: false })
    } catch (error) {
      console.error('❌ Error fetching QA reviews:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch reviews',
        isLoading: false
      })
    }
  },

  createReview: async (reviewData) => {
    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase
        .from('email_qa_reviews')
        .insert(reviewData)
        .select()
        .single()

      if (error) throw error

      // Refresh list
      await get().fetchReviews()

      set({ isLoading: false })
      return data
    } catch (error) {
      console.error('Error creating review:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to create review',
        isLoading: false
      })
      return null
    }
  },

  updateReview: async (id: string, updates: EmailQAUpdate) => {
    set({ isLoading: true, error: null })

    try {
      const { error } = await supabase
        .from('email_qa_reviews')
        .update(updates)
        .eq('id', id)

      if (error) throw error

      // Update local state
      set(state => ({
        reviews: state.reviews.map(review =>
          review.id === id ? { ...review, ...updates } : review
        ),
        selectedReview: state.selectedReview?.id === id
          ? { ...state.selectedReview, ...updates }
          : state.selectedReview,
        isLoading: false
      }))
    } catch (error) {
      console.error('Error updating review:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to update review',
        isLoading: false
      })
    }
  },

  setSelectedReview: (review) => {
    set({ selectedReview: review })
  },

  // Quick actions
  rateReview: async (id: string, rating: number, notes?: string) => {
    const updates: EmailQAUpdate = {
      rating,
      notes: notes || undefined,
      status: 'reviewed'
    }
    await get().updateReview(id, updates)
  },

  approveReview: async (id: string, reviewer: string) => {
    await get().updateReview(id, {
      status: 'approved',
      reviewer
    })
  },

  // Filters
  setFilterStatus: (status) => {
    set({ filterStatus: status })
    get().fetchReviews()
  },

  setFilterRating: (rating) => {
    set({ filterRating: rating })
    get().fetchReviews()
  },

  setFilterReviewer: (reviewer) => {
    set({ filterReviewer: reviewer })
    get().fetchReviews()
  },

  clearFilters: () => {
    set({
      filterStatus: null,
      filterRating: null,
      filterReviewer: null
    })
    get().fetchReviews()
  },

  // Simple analytics
  getAnalytics: () => {
    const { reviews } = get()
    const totalReviews = reviews.length
    const avgRating = reviews.length > 0
      ? reviews.filter(r => r.rating).reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.filter(r => r.rating).length
      : 0

    return {
      totalReviews,
      avgRating: Math.round(avgRating * 10) / 10,
      pendingCount: reviews.filter(r => r.status === 'pending').length,
      approvedCount: reviews.filter(r => r.status === 'approved').length,
      lowQualityCount: reviews.filter(r => r.rating && r.rating <= 4).length
    }
  }
}))

// Helper function to get reviewer from auth
export const getCurrentReviewer = (): string => {
  // This could be enhanced to get from auth store
  return 'Admin' // Placeholder for now
}